// PracticalGuideStudyingAbroad.js
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, BookOpen, Target, CheckCircle, AlertCircle } from 'lucide-react';

const PracticalGuideStudyingAbroad = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Image */}
      <section className="relative py-20 bg-gradient-to-br from-primary to-secondary">
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ 
              backgroundImage: `url(${require('../../assets/blog-1.jpg')})`,
            }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white"
          >
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition"
            >
              <ArrowLeft size={20} />
              Back to Blog
            </Link>
            
            <div className="flex items-center gap-4 text-sm font-light mb-6">
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span>March 10, 2024</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={18} />
                <span>6 min read</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-semibold mb-6 leading-tight">
              Studying Abroad: Working Backwards From the Deadline
            </h1>
            
            <p className="text-xl font-light leading-relaxed text-white/90">
              Country, institution and course are really three separate decisions, and taking them in the wrong order is what makes the whole thing feel unmanageable. Here is the sequence we use with clients, from the budget conversation through to the day you accept an offer.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="prose prose-lg max-w-none"
          >
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-primary mb-4">Three Decisions, Not One</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                People usually describe studying abroad as a single choice, and that is where the difficulty starts. It is at least three: which country you can live and work in afterwards, which institution will have you, and which course is worth the years it will take. Each one narrows the next, so the order you address them in matters more than how much time you spend on any of them.
              </p>
              <p className="text-gray-600 leading-relaxed">
                What follows is the order we work in when someone comes to us at the beginning. It puts money and eligibility at the front, where they belong, and leaves the emotional part, choosing between offers, until the point where you have real options to compare.
              </p>
            </div>

            {/* Step One */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-surface rounded-lg flex items-center justify-center">
                  <BookOpen className="text-primary" size={24} />
                </div>
                <h2 className="text-3xl font-semibold text-primary">Step One: Settle the Money First</h2>
              </div>
              
              <h3 className="text-xl font-semibold text-primary mb-4">Four Numbers Worth Having on Paper</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Almost every abandoned application we see was abandoned for financial reasons, usually discovered late. Before you look at a single prospectus, write down:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Your tuition ceiling:</strong> the highest annual fee you could pay without borrowing on terms you would regret
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Cost of living where you are going:</strong> rent, transport and food in that specific city, not a national average
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Money already committed:</strong> savings, family contributions and any award you have actually been granted rather than one you hope to win
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Your margin if the exchange rate turns:</strong> a ten percent swing over three years is ordinary, not a disaster scenario
                  </div>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-primary mb-4">Then Look Honestly at Your Record</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Your transcript and your test scores set a floor that no amount of enthusiasm in a personal statement will lift. Separate what is fixed from what you can still improve in the months you have left.
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <h4 className="font-semibold text-primary mb-4">Two Kinds of Constraint:</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">Fixed by now</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Grades already awarded</li>
                      <li>• The subjects you took</li>
                      <li>• Where you studied previously</li>
                      <li>• Years of relevant experience</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">Still movable</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Language test scores</li>
                      <li>• Entrance exam results</li>
                      <li>• The quality of your references</li>
                      <li>• How well your statement is argued</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Step Two */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-surface rounded-lg flex items-center justify-center">
                  <Target className="text-primary" size={24} />
                </div>
                <h2 className="text-3xl font-semibold text-primary">Step Two: Build a List in Bands</h2>
              </div>
              
              <h3 className="text-xl font-semibold text-primary mb-4">Eight to Twelve, Grouped by Likelihood</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                A ranking table is a poor shortlist. Group your choices instead by how likely they are to say yes, and make sure you would be content to attend anything in the bottom band.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-primary mb-2">Stretch: two or three</h4>
                  <p className="text-sm text-gray-600">Places where you sit below the usual intake but are not out of range</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-primary mb-2">Well matched: four to six</h4>
                  <p className="text-sm text-gray-600">Places where your profile looks like the profile they normally admit</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-primary mb-2">Secure: two or three</h4>
                  <p className="text-sm text-gray-600">Places you would still be glad to attend and expect to be offered</p>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-primary mb-4">What to Verify Before a Name Goes on the List</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Reputation is the least useful thing to check, because it is the thing everyone checks. These matter more:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <h4 className="font-semibold text-primary mb-4">Worth Confirming in Writing:</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">About the course</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Whether the qualification is recognised at home</li>
                      <li>• Who teaches it and how often</li>
                      <li>• How much of it is assessed by exam</li>
                      <li>• Whether a placement is built in</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">About the country</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Current student visa conditions</li>
                      <li>• Hours you may work while studying</li>
                      <li>• What happens to your status after graduation</li>
                      <li>• Whether dependants can join you</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Step Three */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-primary mb-6">Step Three: Where an Outside View Helps</h2>
              
              <h3 className="text-xl font-semibold text-primary mb-4">The Limits of Doing It Alone</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                You can research most of this yourself, and many people do it well. What is hard to get from the outside is calibration, a sense of whether your list is sensible compared with the hundreds of others being submitted in the same cycle. That is the part worth asking about:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Is my list balanced,</strong> or is every name in it a stretch
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Does my statement answer the question,</strong> or does it only describe me
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Have the requirements changed</strong> since the page I am reading was written
                  </div>
                </li>
              </ul>
            </div>

            {/* Step Four */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-primary mb-6">Step Four: Assembling the File</h2>
              
              <h3 className="text-xl font-semibold text-primary mb-4">What Nearly Every Application Asks For</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Requirements vary, but four items turn up almost everywhere, and three of the four depend on other people. Start those early.
              </p>
              
              <div className="space-y-6 mb-8">
                <div className="border-l-4 border-primary pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Transcripts</h4>
                  <p className="text-gray-600">Issued by each institution you have attended, with a certified translation where the original is not in the language of application.</p>
                </div>
                <div className="border-l-4 border-primary pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Test results</h4>
                  <p className="text-gray-600">Language certification such as IELTS or TOEFL, plus whichever entrance test the level of study calls for. Check how long each score stays valid.</p>
                </div>
                <div className="border-l-4 border-primary pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Your statement</h4>
                  <p className="text-gray-600">One argument, made in your own voice, about why this course and why you. Admissions readers see a great deal of the same phrasing, and yours should not be part of it.</p>
                </div>
                <div className="border-l-4 border-primary pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">References</h4>
                  <p className="text-gray-600">From people who have seen you work recently and can be specific about it. A vague letter from a senior name is worth less than a detailed one from a tutor.</p>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Worth knowing:</h4>
                    <p className="text-gray-600">Give yourself two clear terms before the first deadline. Referees go on leave, registries are slow in summer, and a language test you are unhappy with can only be resat if there is time left to resit it.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step Five */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-primary mb-6">Step Five: Filing It</h2>
              
              <h3 className="text-xl font-semibold text-primary mb-4">When the Windows Open and Shut</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Submission dates differ by country and by institution, but they tend to fall into three patterns:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="font-medium text-gray-800">Early rounds, often binding</span>
                    <span className="text-sm text-gray-600">Autumn</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="font-medium text-gray-800">Main round</span>
                    <span className="text-sm text-gray-600">Winter into early spring</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-800">Rolling intake, decided as received</span>
                    <span className="text-sm text-gray-600">Until places run out</span>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-primary mb-4">The Last Read Through</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Do this once, slowly, on a different day from the one you wrote it:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Confirm the deadline in the local time zone,</strong> not yours
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Read the statement aloud</strong> to catch what your eye skips over
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Check the institution name appears correctly</strong> if you have reused text across applications
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Keep every submission receipt</strong> in one place, with reference numbers
                  </div>
                </li>
              </ul>
            </div>

            {/* Afterwards */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-primary mb-6">Afterwards: The Wait, Then the Choice</h2>
              
              <h3 className="text-xl font-semibold text-primary mb-4">Useful Work While Nothing Is Happening</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Decisions can take months. The gap is more comfortable if you spend it on things that would otherwise land on you all at once later:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Read the visa route in full</strong> for each country still on your list
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Apply for funding you are eligible for now,</strong> since many awards close before offers are released
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Prepare for an admissions interview</strong> if any of your choices use them
                  </div>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-primary mb-4">Comparing Offers</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                If more than one comes back, the temptation is to take the best known name. Look at these instead:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-primary mb-3">The course itself</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Which modules are compulsory</li>
                    <li>• Contact hours per week</li>
                    <li>• Access to supervisors and labs</li>
                    <li>• Whether a placement year is available</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-primary mb-3">Everything around it</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Net cost after any award</li>
                    <li>• Housing, and whether it is guaranteed</li>
                    <li>• Support for international students</li>
                    <li>• Where graduates of that course end up</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Conclusion */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-primary mb-6">A Last Word</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                None of this is difficult on its own. What makes it hard is that the stages overlap, the deadlines belong to other people, and the consequences of a small omission are out of all proportion to the omission itself. Working backwards from the closing date, rather than forwards from enthusiasm, removes most of that risk.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                If you are early enough that nothing is committed, that is the best moment to talk to someone. It is also the cheapest, because the changes that matter most are the ones made before anything has been submitted.
              </p>
              
              <div className="bg-gradient-to-r from-primary to-secondary p-8 rounded-lg text-white">
                <h3 className="text-2xl font-semibold mb-4">Want a Second Look at Your Plan?</h3>
                <p className="mb-6 text-white/90">
                  Ivipass will go through your shortlist, your timings and your documents with you, and tell you plainly where the weak points are.
                </p>
                <button className="bg-white text-primary px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition">
                  Arrange a Consultation
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PracticalGuideStudyingAbroad;
